import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, PagePanel, Text } from 'edenred-ui';
import { useParams } from 'react-router-dom';

import { IBenefits } from '@epi-models/containers/ManageBenefits';
import { OnboardingSupportInformation } from '@epi-components/OnboardingSupportInformation';
import OnboardingStepper from '@epi-components/OnboardingStepper/OnboardingStepper';
import { useGetDetailsQuery } from '@epi-api/user';

import { getDailyLimitSelectFieldOptions } from './helpers';
import {
  AddBenefitGroupTile,
  BenefitGroupDetail,
  BenefitGroupTiles,
  BenefitInformationalModal,
  CreatedGroupNotifications,
  MassageWellbeingInfoBar
} from './components';
import {
  useGetAmounts,
  useManageBenefitGroups,
  useManageBenefitGroupsModal
} from './hooks';

export const ManageBenefitGroups = () => {
  const { t } = useTranslation();
  const { benefitGroup } = useParams<{ benefitGroup: IBenefits }>();
  const {
    benefitGroups,
    isBenefitGroupsLoaded,
    isBenefitGroupsError,
    isMassageOrWellbeing,
    isBenefitAvailable,
    shouldShowAddBenefitTile
  } = useManageBenefitGroups();
  const { data: customerData } = useGetDetailsQuery();
  const {
    detail,
    setDetail,
    handleViewDetail,
    handleCloseDetail,
    handleEditDetail,
    handleUpdateBenefitGroup,
    handleOpenCreateDatail,
    handleCreateBenefitGroup,
    handleCancelDetail
  } = useManageBenefitGroupsModal();
  const amount = useGetAmounts();

  const isOnboardingDone =
    customerData?.isBenefitsSet &&
    customerData?.isEmployeesSet &&
    (customerData?.isMoneyTransfered || customerData?.isLoadInitiated);

  const showInfoBar = isMassageOrWellbeing && isOnboardingDone;

  const currentBenefitGroup = useMemo(
    () => benefitGroups.find(group => group.groupId === detail.currentGroupId),
    [detail.currentGroupId, benefitGroups]
  );

  const dailyLimitOptionsIncludingCurrentlySelected = useMemo(() => {
    return getDailyLimitSelectFieldOptions(
      amount,
      currentBenefitGroup?.maxAmountPerPeriod
    );
  }, [currentBenefitGroup]);

  const [showInformationModal, setShowInformationModal] = useState(false);

  const handleUpdate = (groupData: any, showModal: boolean) => {
    handleUpdateBenefitGroup(groupData);
    setShowInformationModal(() => showModal);
  };

  const handleCloseModal = () => {
    setShowInformationModal(false);
  };

  return (
    <Box>
      <OnboardingStepper />
      <PagePanel
        title={t(`components.manage_benefits.new.benefits.${benefitGroup}`)}
        backIcon
        backIconLink={t('components.manage_benefit_lunch.back_link')}
      >
        {(detail.createdGroupSuggestion || detail.createdGroupNotification) && (
          <CreatedGroupNotifications
            detail={detail}
            isBenefitGroupsError={isBenefitGroupsError}
            setDetail={setDetail}
          />
        )}
        {showInfoBar && <MassageWellbeingInfoBar benefitGroup={benefitGroup} />}
        {!(
          detail.createdGroupNotification || detail.createdGroupSuggestion
        ) && (
          <Box maxWidth={860} pb={5} id="benefit-group-details-subtitle">
            <Text light>{t('components.manage_benefit_lunch.subtitle')}</Text>
          </Box>
        )}
        {isBenefitGroupsLoaded && (
          <Box
            display="flex"
            flexWrap="wrap"
            id={`employee-benefits-${benefitGroup}-group-tiles`}
          >
            <BenefitGroupTiles
              benefitGroups={benefitGroups}
              handleViewDetail={handleViewDetail}
              handleEditDetail={handleEditDetail}
            />
            {shouldShowAddBenefitTile && (
              <AddBenefitGroupTile onClick={handleOpenCreateDatail} />
            )}
          </Box>
        )}
        {isBenefitAvailable && (
          <BenefitGroupDetail
            detail={detail}
            onClose={handleCloseDetail}
            onSave={handleUpdate}
            onCancel={handleCancelDetail}
            onCreate={handleCreateBenefitGroup}
            onEdit={handleEditDetail}
            benefitGroup={currentBenefitGroup}
            benefitType={benefitGroup}
            dailyLimitOptions={dailyLimitOptionsIncludingCurrentlySelected}
            key={currentBenefitGroup?.groupId}
          />
        )}
        <BenefitInformationalModal
          showInformationModal={showInformationModal}
          closeModal={handleCloseModal}
        />
      </PagePanel>
      <OnboardingSupportInformation />
    </Box>
  );
};
